import { z } from "zod";
import { localizedTextSchema } from "./intake-template";

// TES-207 — editable automated coach messages: a global version history + a
// per-patient override, layered over the seeded `coach_message` Article (which
// is never mutated). Body is LocalizedText {sv,en}, matching the article shape.

// Where a resolved coach-message body came from — for the admin to show whether
// a message is still the seeded default, globally edited, or patient-overridden.
export const COACH_MESSAGE_SOURCES = ["seeded", "version", "override"] as const;
export const coachMessageSourceSchema = z.enum(COACH_MESSAGE_SOURCES);
export type CoachMessageSource = z.infer<typeof coachMessageSourceSchema>;

// Edit body (a global edit OR a per-patient override write).
export const editCoachMessageSchema = z.object({
  body: localizedTextSchema,
});
export type EditCoachMessage = z.infer<typeof editCoachMessageSchema>;

// Roll a global message back to a historical version (appends a NEW version
// equal to it — history is never destroyed).
export const rollbackCoachMessageSchema = z.object({
  versionNumber: z.number().int().positive(),
});
export type RollbackCoachMessage = z.infer<typeof rollbackCoachMessageSchema>;

export const coachMessageVersionDtoSchema = z.object({
  id: z.string(),
  messageKey: z.string(),
  body: localizedTextSchema,
  editedByEmployeeId: z.string().nullable(),
  versionNumber: z.number().int(),
  createdAt: z.string(),
});
export type CoachMessageVersionDto = z.infer<
  typeof coachMessageVersionDtoSchema
>;

export const coachMessageOverrideDtoSchema = z.object({
  id: z.string(),
  patientId: z.string(),
  messageKey: z.string(),
  body: localizedTextSchema,
  editedByEmployeeId: z.string().nullable(),
  updatedAt: z.string(),
});
export type CoachMessageOverrideDto = z.infer<
  typeof coachMessageOverrideDtoSchema
>;

// One row in the admin coach-message list: the message key, its EFFECTIVE
// (globally-resolved) body + where it came from, and the latest version number.
export const coachMessageDtoSchema = z.object({
  messageKey: z.string(),
  body: localizedTextSchema,
  source: coachMessageSourceSchema,
  latestVersion: z.number().int().nullable(),
});
export type CoachMessageDto = z.infer<typeof coachMessageDtoSchema>;
